// ============================================================================
// CONFIG LIST EDITOR
// ============================================================================
// Multi-value list editor for config settings with dataType "List".
// Items can be toggled active/inactive — they are never hard-deleted,
// so existing API values are preserved and can be reactivated.

import React, { useCallback } from 'react';
import type { ConfigListItem } from 'api/types/config.types';

interface ConfigListEditorProps {
  values: ConfigListItem[];
  onChange: (values: ConfigListItem[]) => void;
  /** Set of value strings that originated from the API — these items cannot be renamed */
  originalValues?: Set<string>;
  /** Optional hint shown inside new-item input placeholder */
  unitLabel?: string;
  isReadOnly?: boolean;
}

const ConfigListEditor: React.FC<ConfigListEditorProps> = ({
  values,
  onChange,
  originalValues,
  unitLabel,
  isReadOnly = false,
}) => {
  // Auto-format to UPPER_SNAKE_CASE on blur: trim, replace spaces/hyphens with _, uppercase
  const formatValue = (raw: string): string =>
    raw.trim().replace(/[\s-]+/g, '_').toUpperCase();

  const handleItemChange = useCallback(
    (index: number, newValue: string) => {
      const updated = [...values];
      updated[index] = { ...updated[index], value: newValue };
      onChange(updated);
    },
    [values, onChange]
  );

  const handleItemBlur = useCallback(
    (index: number) => {
      const formatted = formatValue(values[index].value);
      if (formatted !== values[index].value) {
        const updated = [...values];
        updated[index] = { ...updated[index], value: formatted };
        onChange(updated);
      }
    },
    [values, onChange]
  );

  const handleToggleActive = useCallback(
    (index: number) => {
      const updated = [...values];
      updated[index] = { ...updated[index], isActive: !updated[index].isActive };
      onChange(updated);
    },
    [values, onChange]
  );

  const handleAdd = useCallback(() => {
    onChange([...values, { value: '', isActive: true }]);
  }, [values, onChange]);

  return (
    <div className="config-list-editor">
      {values.map((item, index) => (
        <div
          key={index}
          className={`config-list-item${!item.isActive ? ' config-list-item--inactive' : ''}`}
        >
          <input
            type="text"
            value={item.value}
            onChange={(e) => handleItemChange(index, e.target.value)}
            onBlur={() => handleItemBlur(index)}
            disabled={isReadOnly || !item.isActive || (originalValues?.has(item.value) ?? false)}
            placeholder={unitLabel ? `Value (${unitLabel})` : 'Enter new value'}
          />
          {!isReadOnly && (
            <label
              className="config-list-toggle"
              title={item.isActive ? 'Active — click to deactivate' : 'Inactive — click to activate'}
            >
              <input
                type="checkbox"
                checked={item.isActive}
                onChange={() => handleToggleActive(index)}
              />
              <span className="config-toggle-slider" />
            </label>
          )}
        </div>
      ))}
      {!isReadOnly && (
        <button type="button" className="config-list-add-btn" onClick={handleAdd}>
          + Add item
        </button>
      )}
    </div>
  );
};

export default ConfigListEditor;
